import type { PostHog, PostHogInterface } from 'posthog-js';

export const TELEMETRY_IDLE_FALLBACK_MS = 2500;

export interface ToolFeedbackPayload {
  rating: 'helpful' | 'unhelpful';
  reason?: string;
  comment?: string;
}

export interface ToolExecutionPayload {
  success: boolean;
  durationMs?: number;
  error?: string;
  metadata?: Record<string, unknown>;
}

type QueuedEvent =
  | { type: 'event'; name: string; properties?: Record<string, unknown> }
  | { type: 'exception'; error: Error; properties?: Record<string, unknown> };

let posthogInstance: PostHog | PostHogInterface | null = null;
let isInitializing = false;
let isInitialized = false;
const eventQueue: QueuedEvent[] = [];

function flushQueue() {
  if (!posthogInstance || !isInitialized) return;
  while (eventQueue.length > 0) {
    const item = eventQueue.shift();
    if (!item) break;
    if (item.type === 'event') {
      posthogInstance.capture(item.name, item.properties);
    } else if (item.type === 'exception') {
      posthogInstance.captureException(item.error, item.properties);
    }
  }
}

/**
 * Dynamically loads and initializes PostHog after the main browser paint cycle.
 * This guarantees zero impact on Core Web Vitals, TBT, and initial load performance.
 */
// Kill switch: PostHog ingest is misconfigured (EU/US region mismatch) and is
// dropping all events. Disabled until the region routing is fixed — see vercel.json.
const POSTHOG_DISABLED = true;

export async function loadPostHog(): Promise<PostHog | PostHogInterface | null> {
  if (POSTHOG_DISABLED) return null;
  if (posthogInstance) return posthogInstance;
  if (isInitializing) return null;

  const apiKey = import.meta.env.VITE_POSTHOG_KEY;
  if (!apiKey) {
    return null;
  }

  isInitializing = true;
  try {
    const { default: posthog } = await import('posthog-js');
    const apiHost = import.meta.env.VITE_POSTHOG_HOST || '/ingest';

    posthog.init(apiKey, {
      api_host: apiHost,
      ui_host: 'https://eu.posthog.com',
      person_profiles: 'identified_only',
      capture_pageview: true,
      capture_pageleave: true,
      autocapture: true,
      session_recording: {
        maskAllInputs: false,
        maskInputOptions: {
          password: true,
        },
      },
      loaded: (ph) => {
        posthogInstance = ph;
        isInitialized = true;
        flushQueue();
      },
    });

    posthogInstance = posthog;
    isInitialized = true;
    flushQueue();
    return posthog;
  } catch {
    // Graceful fallback: never crash user experience on telemetry network errors
    return null;
  } finally {
    isInitializing = false;
  }
}

/**
 * Track technical errors from tool components or error boundaries.
 */
export function trackToolError(
  toolId: string,
  error: Error | string,
  context?: Record<string, unknown>,
) {
  const errorObj = typeof error === 'string' ? new Error(error) : error;
  const properties = {
    tool_id: toolId,
    error_message: errorObj.message,
    error_stack: errorObj.stack,
    ...context,
  };

  if (posthogInstance && isInitialized) {
    posthogInstance.captureException(errorObj, properties);
    posthogInstance.capture('tool_error', properties);
  } else {
    eventQueue.push({ type: 'exception', error: errorObj, properties });
    eventQueue.push({ type: 'event', name: 'tool_error', properties });
    loadPostHog().catch(() => {});
  }

  // Also sync to Google Analytics
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'tool_error', {
      tool_id: toolId,
      error_message: errorObj.message,
      error_stack: errorObj.stack,
    });
  }
}

/**
 * Track an execution or conversion attempt for any tool.
 */
export function trackToolExecution(toolId: string, payload: ToolExecutionPayload) {
  const properties = {
    tool_id: toolId,
    success: payload.success,
    duration_ms: payload.durationMs,
    error: payload.error,
    ...payload.metadata,
  };

  if (posthogInstance && isInitialized) {
    posthogInstance.capture('tool_execution', properties);
  } else {
    eventQueue.push({ type: 'event', name: 'tool_execution', properties });
    loadPostHog().catch(() => {});
  }

  // Also sync to Google Analytics if active
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'tool_execution', properties);
  }
}

/**
 * Track user satisfaction feedback (Thumbs up / down, rating, feedback text).
 */
export function trackToolFeedback(toolId: string, payload: ToolFeedbackPayload) {
  const properties = {
    tool_id: toolId,
    rating: payload.rating,
    reason: payload.reason,
    comment: payload.comment,
  };

  if (posthogInstance && isInitialized) {
    posthogInstance.capture('tool_feedback', properties);
  } else {
    eventQueue.push({ type: 'event', name: 'tool_feedback', properties });
    loadPostHog().catch(() => {});
  }

  // Also sync to Google Analytics
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'tool_feedback', properties);
  }
}

let globalListenersAttached = false;
function setupGlobalErrorListeners() {
  if (globalListenersAttached || typeof window === 'undefined') return;
  globalListenersAttached = true;

  window.addEventListener('error', (event) => {
    // Ignore browser extension script errors
    if (
      event.filename &&
      (event.filename.includes('extension://') || event.filename.includes('chrome-extension://'))
    ) {
      return;
    }
    const errorObj =
      event.error instanceof Error ? event.error : new Error(event.message || 'Script error');
    trackToolError('global', errorObj, {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const { reason } = event;
    const errorObj =
      reason instanceof Error ? reason : new Error(String(reason || 'Unhandled Promise Rejection'));
    trackToolError('global', errorObj, {
      unhandledRejection: true,
    });
  });
}

/**
 * Initializes global telemetry without blocking the main event loop or Core Web Vitals.
 * Defers SDK loading until first user interaction (touch/click/scroll/keypress)
 * or a bounded idle timeout, guaranteeing 100% PageSpeed / Lighthouse performance scores.
 */
export function initTelemetry(isProduction = import.meta.env.PROD) {
  if (typeof window === 'undefined') return;

  // Set up global error monitoring for uncaught exceptions immediately
  setupGlobalErrorListeners();

  if (POSTHOG_DISABLED) return;

  const apiKey = import.meta.env.VITE_POSTHOG_KEY;
  // If no key is configured in dev or prod, telemetry operates in safe no-op mode
  if (!apiKey && !isProduction) return;

  let hasTriggered = false;
  const onInteraction = () => {
    if (hasTriggered) return;
    hasTriggered = true;
    const events = ['pointerdown', 'touchstart', 'scroll', 'keydown'];
    events.forEach((evt) => {
      window.removeEventListener(evt, onInteraction);
    });
    loadPostHog().catch(() => {});
  };

  const events = ['pointerdown', 'touchstart', 'scroll', 'keydown'];
  events.forEach((evt) => {
    window.addEventListener(evt, onInteraction, { once: true, passive: true });
  });

  // Bounded idle fallback timer so idle background visits still get recorded
  window.setTimeout(onInteraction, 6000);
}
